import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Fingerprint, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startRegistration } from "@simplewebauthn/browser";

// One-tap "Enable fingerprint on this device?" prompt shown once after a password login.
export default function FingerprintPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem("fp_prompt_pending") !== "1") return;
    localStorage.removeItem("fp_prompt_pending");
    const dismissKey = `fp_prompt_dismissed_${user.username}`;
    if (localStorage.getItem(dismissKey) === "1") return;

    (async () => {
      try {
        if (!window.PublicKeyCredential || !window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return;
        const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (!available) return;
        const { data } = await api.get("/webauthn/status");
        if (data?.allowed && (data?.enrolled || 0) === 0) setOpen(true);
      } catch { /* silently skip */ }
    })();
  }, [user]);

  const dismiss = () => {
    if (user) localStorage.setItem(`fp_prompt_dismissed_${user.username}`, "1");
    setOpen(false);
  };

  const enroll = async () => {
    setBusy(true);
    try {
      const opts = (await api.post("/webauthn/register/options")).data;
      const cred = await startRegistration({ optionsJSON: opts });
      await api.post("/webauthn/register/verify", cred);
      if (user) localStorage.setItem(`fp_prompt_dismissed_${user.username}`, "1");
      toast.success("Fingerprint / Face sign-in is now enabled on this device");
      setOpen(false);
    } catch (e) {
      const msg = e?.name === "NotAllowedError" ? "Cancelled — you can enable it later in Settings → Login." : (apiErr(e?.response?.data?.detail) || e.message || "Could not enable biometric here");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent data-testid="fp-prompt-dialog" className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 mb-2"><Fingerprint size={28} /></div>
          <DialogTitle className="text-center">Enable fingerprint on this device?</DialogTitle>
          <DialogDescription className="text-center">
            Sign in faster next time using this device's fingerprint or face unlock. You can turn it off anytime in Settings → Login.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button onClick={enroll} disabled={busy} className="w-full h-11" data-testid="fp-prompt-enable-btn">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Fingerprint size={16} className="mr-2" />}Enable fingerprint
          </Button>
          <Button variant="ghost" onClick={dismiss} className="w-full" data-testid="fp-prompt-dismiss-btn">Not now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
